import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  alpha,
} from '@mui/material';
import { CloudUpload, PictureAsPdf, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../../contexts/ToastContext';
import { apiService } from '../../../services/api';
import { useTheme } from '@mui/material/styles';

export const NomenclaturaDocumentManagement: React.FC = () => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useToast();
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [hasDocument, setHasDocument] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const info = await apiService.getNomenclaturaPdfInfo();
      setHasDocument(info.has_document);
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Error al cargar estado del documento');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      showError('Solo se permiten archivos PDF');
      return;
    }
    try {
      setUploading(true);
      await apiService.uploadNomenclaturaPdf(file);
      setHasDocument(true);
      showSuccess(t('admin.nomenclaturaDoc.uploadSuccess', 'Documento subido correctamente'));
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Error al subir el documento');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await apiService.deleteNomenclaturaPdf();
      setHasDocument(false);
      showSuccess(t('admin.nomenclaturaDoc.deleteSuccess', 'Documento eliminado'));
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Error al eliminar el documento');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card variant="outlined" sx={{ maxWidth: 560 }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <PictureAsPdf color="primary" />
          <Typography variant="h6" fontWeight={600}>
            {t('admin.nomenclaturaDoc.title', 'Guía de Nomenclatura de Sensores')}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('admin.nomenclaturaDoc.description', 'Este documento se mostrará en la subida de archivos de sensores y en el listado de sensores de cada ensayo, para que los usuarios conozcan la nomenclatura recomendada.')}
        </Typography>

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        {hasDocument ? (
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.success.main, 0.08),
              border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
            }}
          >
            <Alert severity="success" icon={<PictureAsPdf />} sx={{ mb: 2 }}>
              {t('admin.nomenclaturaDoc.uploaded', 'Documento de nomenclatura subido')}
            </Alert>
            <Box display="flex" gap={1}>
              <Tooltip title={t('admin.nomenclaturaDoc.replace', 'Reemplazar documento')}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
                  onClick={handleFileSelect}
                  disabled={uploading}
                >
                  {uploading ? t('common.loading') : t('admin.nomenclaturaDoc.replace', 'Reemplazar')}
                </Button>
              </Tooltip>
              <Tooltip title={t('admin.nomenclaturaDoc.remove', 'Eliminar documento')}>
                <IconButton
                  color="error"
                  size="small"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? <CircularProgress size={20} /> : <Delete />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ) : (
          <Button
            variant="contained"
            startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
            onClick={handleFileSelect}
            disabled={uploading}
          >
            {uploading ? t('common.loading') : t('admin.nomenclaturaDoc.upload', 'Subir PDF')}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
